/**
 * Cuts of meat charts — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_kitchen_charts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const cutsOfMeatChartsPack: ToilePack = {
    slug: 'cuts-of-meat-charts',
    category: 'home',
    name: 'Cuts of meat charts',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'beef', label: 'Beef cuts', subject: 'a beef cuts chart, the side outline of a steer divided into chuck, rib, short loin, sirloin, round, brisket, plate and flank, each section named'},
        {key: 'pork', label: 'Pork cuts', subject: 'a pork cuts chart, the side outline of a pig divided into shoulder, loin, belly, ham, jowl and hock, each section named'},
        {key: 'lamb', label: 'Lamb cuts', subject: 'a lamb cuts chart, the side outline of a lamb divided into neck, shoulder, rack, loin, breast, leg and shank, each section named'},
    ],
    styles: [
        {key: 'butcher', label: 'Butcher shop', style: "a vintage butcher's shop diagram with bold black outlines and hatched sections", palette: 'black ink and red on cream paper', framing: 'the full landscape page used, the chart centred with a white margin on every side'},
        {key: 'infographic', label: 'Infographic', style: 'a clean modern infographic with thin rules, a bold header band and tidy columns', palette: 'navy and white with one orange accent', framing: 'the full landscape page used, the chart centred with a white margin on every side'},
        {key: 'chalk', label: 'Chalkboard', style: 'a hand-drawn chalk diagram with rough chalk strokes', palette: 'white and pastel chalk on a dark green chalkboard', framing: 'the diagram filling the board like a kitchen chalkboard, a margin of bare board on every side'},
    ],
    directions: ['chicken', 'goat', 'venison'],
    directionHint: 'try: chicken',
    seo: {
        title: 'Cuts of Meat Charts: Beef, Pork, Lamb',
        metaDescription: "Nine butcher's cuts charts: the named cuts of beef, pork and lamb on a side-view outline of each animal, in three styles.",
        h1: 'Cuts of meat charts: beef, pork and lamb cuts',
        tagline: 'Nine pictures: the side view of a steer, a pig and a lamb, each divided into its named cuts, in three styles.',
        intro: "Butcher's cuts charts for a kitchen wall or a shop. Each picture shows the side outline of an animal divided into its named cuts: beef into chuck, rib, short loin, sirloin, round, brisket, plate and flank; pork into shoulder, loin, belly, ham, jowl and hock; and lamb into neck, shoulder, rack, loin, breast, leg and shank. Type a different subject, such as goat or venison, and the same three styles carry the request.",
        useCases: [
            'a kitchen wall in a home',
            'a butcher shop display',
            "a barbecue cook's reference",
            'a class on butchery',
        ],
        faq: [
            {q: 'Which animals are shown?', a: 'A steer, a pig and a lamb, each in side view with its cuts named on the outline.'},
            {q: 'Is the sheet landscape or portrait?', a: 'The cuts charts are landscape pages, wider than they are tall, so they fit a wall or a shop board.'},
            {q: 'Can I get a chart for another animal?', a: 'Type the animal as a direction, such as goat or venison. It is a request, not a promised result.'},
            {q: 'Which style suits a shop?', a: 'The butcher shop style, with bold black outlines and hatched sections, reads like an old shop sign; infographic is the cleanest.'},
        ],
    },
}
